import { useMemo, useState } from "react";
import dayjs from "dayjs";
import clsx from "clsx";
import { Seg } from "./ui";

/** A month / week calendar for meetings and events. Click an item to open it. */
export type CalItem = { id: string; at: string; title: string; sub?: string | null; tone?: "default" | "muted" | "alert" | "gold"; allDay?: boolean };

export function CalendarView({ items, month, onMonth, onOpen }: { items: CalItem[]; month: dayjs.Dayjs; onMonth: (m: dayjs.Dayjs) => void; onOpen: (id: string) => void }) {
  const [mode, setMode] = useState<"month" | "week">("month");
  const [weekOf, setWeekOf] = useState(() => dayjs().startOf("week").add(1, "day"));   // Monday
  const [dayOpen, setDayOpen] = useState<string | null>(null);
  const byDay = useMemo(() => {
    const m: Record<string, CalItem[]> = {};
    for (const it of items) (m[dayjs(it.at).format("YYYY-MM-DD")] ??= []).push(it);
    for (const k of Object.keys(m)) m[k].sort((a, b) => a.at.localeCompare(b.at));
    return m;
  }, [items]);
  const today = dayjs().format("YYYY-MM-DD");
  // Monday-first grid
  const start = mode === "month" ? month.startOf("month").subtract((month.startOf("month").day() + 6) % 7, "day") : weekOf;
  const days = Array.from({ length: mode === "month" ? 42 : 7 }, (_, i) => start.add(i, "day"));
  const label = mode === "month" ? month.format("MMMM YYYY") : `${weekOf.format("MMM D")} – ${weekOf.add(6, "day").format("MMM D, YYYY")}`;
  const move = (n: number) => {
    if (mode === "month") onMonth(month.add(n, "month"));
    else { const w = weekOf.add(n, "week"); setWeekOf(w); if (!w.isSame(month, "month")) onMonth(w.startOf("month")); }
  };
  const goToday = () => { onMonth(dayjs().startOf("month")); setWeekOf(dayjs().startOf("week").add(1, "day")); };
  const chip = (it: CalItem, full?: boolean) => (
    <button key={it.id} className={clsx("cal-item", it.tone && `t-${it.tone}`)} onClick={() => onOpen(it.id)} title={`${it.title}${it.sub ? ` · ${it.sub}` : ""}`}>
      {!it.allDay && <b>{dayjs(it.at).format("h:mma").replace(":00", "")}</b>} {it.title}{full && it.sub && <span className="muted"> · {it.sub}</span>}
    </button>
  );
  return (
    <div className="cal">
      <div className="cal-bar">
        <button className="btn small ghost" onClick={() => move(-1)} aria-label="Previous">‹</button>
        <b className="cal-label">{label}</b>
        <button className="btn small ghost" onClick={() => move(1)} aria-label="Next">›</button>
        <button className="btn small ghost" onClick={goToday}>Today</button>
        <span style={{ flex: 1 }} />
        <Seg value={mode} onChange={(v) => { setMode(v); if (v === "week") setWeekOf((month.isSame(dayjs(), "month") ? dayjs() : month.startOf("month")).startOf("week").add(1, "day")); }}
          options={[{ value: "month", label: "Month" }, { value: "week", label: "Week" }]} />
      </div>
      <div className={clsx("cal-grid", mode === "week" && "week")}>
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="cal-dow">{d}</div>)}
        {days.map((d) => {
          const k = d.format("YYYY-MM-DD");
          const list = byDay[k] ?? [];
          const cap = mode === "week" ? 50 : 3;
          return (
            <div key={k} className={clsx("cal-day", mode === "month" && !d.isSame(month, "month") && "out", k === today && "today")}>
              <div className="cal-num">{mode === "week" ? d.format("ddd D") : d.date()}</div>
              {list.slice(0, cap).map((it) => chip(it, mode === "week"))}
              {list.length > cap && <button className="linkish small" onClick={() => setDayOpen(k)}>+{list.length - cap} more</button>}
            </div>
          );
        })}
      </div>
      {dayOpen && (
        <div className="cal-pop" onClick={() => setDayOpen(null)}>
          <div className="cal-pop-card" onClick={(e) => e.stopPropagation()}>
            <div className="cal-bar"><b>{dayjs(dayOpen).format("dddd, MMMM D")}</b><span style={{ flex: 1 }} /><button className="btn small ghost" onClick={() => setDayOpen(null)}>Close</button></div>
            <div className="stack" style={{ gap: 4 }}>{(byDay[dayOpen] ?? []).map((it) => chip(it, true))}</div>
          </div>
        </div>
      )}
    </div>
  );
}
